import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import Decimal from 'decimal.js';
import moment from 'moment';
import { Address, AddressContact } from '../models/Address';
import { CheckoutQuestion, CheckoutQuestionForm } from '../models/CheckoutQuestion';
import { CheckoutSettings, RegionDefinition, RestrictAddressMode } from '../models/CheckoutSession';
import { FulfillmentMethodType } from '../models/FulfillmentMethod';
import { AddressSource } from '../models/FulfillmentQuote';
import { Fulfillment } from '../models/Order';
import { OrderItem } from '../models/OrderItem';
import { PaymentMethodSettings } from '../models/PaymentMethodSettings';
import { Question } from '../models/Question';

export function calculateConvenienceFee(
  amount: Decimal,
  percentage: number,
  taxable: boolean,
  taxRate: number,
) {
  const fee = amount.times(new Decimal(percentage)).div(100).toDP(2);
  if (taxable) {
    const tax = fee.times(new Decimal(taxRate)).div(100).toDP(2);
    return { tax, fee };
  } else {
    return { tax: new Decimal(0), fee };
  }
}

export function getItemColorText(item: OrderItem): string {
  const set = new Set(item.rows.map((r) => r.color));
  if (set.size === 1) {
    return item.rows[0].color;
  } else {
    return 'Multiple';
  }
}

export function getItemSizesText(item: OrderItem): string {
  const set = new Set(item.rows.map((r) => r.size));
  if (set.size === 1) {
    return item.rows[0].size;
  } else {
    return 'Multiple';
  }
}

export function getAddressString(address: Address): string {
  if (!address) return 'N/A';
  return `${address.street1}, ${address.street2 ? `${address.street2}, ` : ''}${address.city}, ${
    address.state
  } ${address.postalCode}`;
}

export function getValidFulfillments(settings: CheckoutSettings, fulfillments: Fulfillment[]) {
  return fulfillments.filter((f) => {
    if (f.method.type !== FulfillmentMethodType.SHIPPING) {
      return true;
    }
    if (settings.restrictShippingAddressMode === RestrictAddressMode.SHIPPING_LOCATIONS) {
      return f.addressSource === AddressSource.SHIPPING_LOCATION;
    } else if (
      settings.restrictShippingAddressMode === RestrictAddressMode.SPECIFIC_SHIPPING_LOCATION
    ) {
      return (
        f.addressSource === AddressSource.SHIPPING_LOCATION &&
        f.savedAddressId === settings.restrictShippingAddressId
      );
    } else {
      return true;
    }
  });
}

export function getNotEmpty(value: string | null | undefined) {
  return !!value ? value : null;
}

export function getFirstValue(values: (string | null | undefined)[]) {
  return values.find((v) => getNotEmpty(v)) ?? '';
}

export function getEtaString(fulfillment: Fulfillment): string {
  if (
    fulfillment.minFulfillmentDate &&
    fulfillment.maxFulfillmentDate &&
    fulfillment.minFulfillmentDate !== fulfillment.maxFulfillmentDate
  ) {
    return `${moment(fulfillment.minFulfillmentDate).format('M/D/YY')} - ${moment(
      fulfillment.minFulfillmentDate,
    ).format('M/D/YY')}`;
  } else if (fulfillment.minFulfillmentDate) {
    return moment(fulfillment.minFulfillmentDate).format('M/D/YY');
  } else if (fulfillment.maxFulfillmentDate) {
    return moment(fulfillment.maxFulfillmentDate).format('M/D/YY');
  } else {
    return 'N/A';
  }
}

export function getError(error: FetchBaseQueryError | SerializedError | undefined) {
  if (error === undefined) {
    return undefined;
  }
  if (error) {
    if ('data' in error) {
      const message = (error as any).data?.message;
      if (typeof message === 'string') {
        return message;
      } else if (Array.isArray(message)) {
        return message.join(', ');
      }
    }
  }
  return 'An error occurred';
}

export function getFormQuestions(
  questions: Question[],
  checkoutQuestions: CheckoutQuestion[],
): CheckoutQuestionForm[] {
  return questions.map((q) => {
    const existingQuestion = checkoutQuestions.find((cq) => cq.questionId === q._id);
    return {
      ...q,
      questionId: q._id,
      choiceValue: existingQuestion?.choiceValue ?? '',
      value: existingQuestion?.value ?? '',
      choices: q.choices,
    };
  });
}

export function getItemIds(items: { itemId: string; amount: number }[], amount: number) {
  let left = new Decimal(amount);
  const itemIds: string[] = [];
  for (const item of items) {
    if (left.greaterThan(0) && item.itemId) {
      left = left.minus(item.amount);
      itemIds.push(item.itemId);
    } else if (!item.itemId) {
      continue;
    } else {
      break;
    }
  }
  return itemIds;
}

export function getRestrictedPaymentMessage(settings: PaymentMethodSettings) {
  if (!settings.applyToProduct) {
    return 'This payment method cannot be applied to products.';
  } else if (!settings.applyToShipping) {
    return 'This payment method cannot be applied to shipping.';
  } else {
    return 'Some items cannot be paid with this method.';
  }
}

export function isCanadaCountry(value: string) {
  return value === 'CA' || value === 'Canada';
}

// Returns fulfillments that could be used for billing addresses given the restrictions
export function getValidFulfillmentsForBilling(
  fulfillments: Fulfillment[],
  restrictAddressMode: RestrictAddressMode,
  restrictAddressId?: string,
  restrictedCountries?: string[],
  regionDefinitions?: RegionDefinition[],
) {
  return fulfillments.filter((f) => {
    if (f.method.type !== FulfillmentMethodType.SHIPPING) {
      return false;
    }
    const countryCheck =
      restrictedCountries === undefined || restrictedCountries.includes(f.address?.country ?? '');
    const rd = regionDefinitions?.find((rd) => rd.countryCode === f.address?.country);
    const regionCheck = rd?.regions === undefined || rd?.regions?.includes(f.address?.state ?? '');
    if (!countryCheck || !regionCheck) {
      return false;
    }
    if (restrictAddressMode === RestrictAddressMode.SHIPPING_LOCATIONS) {
      return f.addressSource === AddressSource.SHIPPING_LOCATION;
    } else if (restrictAddressMode === RestrictAddressMode.SPECIFIC_SHIPPING_LOCATION) {
      return (
        f.addressSource === AddressSource.SHIPPING_LOCATION &&
        f.savedAddressId === restrictAddressId
      );
    } else {
      return true;
    }
  });
}

export function getValidContact(
  candidate: AddressContact,
  { firstName, lastName, email }: { firstName: string; lastName: string; email: string },
  lockName: boolean = false,
  lockEmail: boolean = false,
) {
  const updatedContact = { ...candidate };
  if (lockName) {
    updatedContact.firstName = firstName;
    updatedContact.lastName = lastName;
  }
  if (lockEmail) {
    updatedContact.email = email;
  }
  return updatedContact;
}
